import { useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../../api/client'
import { keys } from '../../api/keys'
import { emit } from '../../platform/analytics/analytics'

/**
 * `DELETE /devices/{id}` — sign ONE other device out. Success drops its row (the devices
 * query is re-read); failure leaves the row and surfaces on the mutation.
 *
 * `networkMode: 'always'`: the default pauses an offline mutation forever, which would
 * leave the button on "Signing out…" with nothing to tell the user.
 */
export function useSignOutDevice() {
  const client = useQueryClient()
  return useMutation({
    networkMode: 'always',
    mutationFn: async (deviceId: string) => {
      const { error } = await api.DELETE('/api/v1/devices/{device_id}', {
        params: { path: { device_id: deviceId } },
      })
      if (error !== undefined) throw new Error('Could not sign that device out.')
    },
    onSuccess: async () => {
      emit('device_signed_out', { scope: 'one' })
      await client.invalidateQueries({ queryKey: keys.devices.all() })
    },
  })
}

/**
 * `DELETE /devices` — sign out everywhere. The server also revokes THIS session, so the
 * caller signs out locally on success (the dialog says so). No cache invalidation: the
 * local sign-out clears the whole cache.
 */
export function useSignOutEverywhere() {
  return useMutation({
    networkMode: 'always',
    mutationFn: async () => {
      const { error } = await api.DELETE('/api/v1/devices')
      if (error !== undefined) throw new Error('Could not sign out everywhere.')
    },
    onSuccess: () => emit('device_signed_out', { scope: 'all' }),
  })
}
